import { isLoggedIn, ServiceError } from "@shapr3d/frontend-lib";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useSearchParams } from "react-router-dom";

import { SyncRequestClient } from "../stores/requestClient";
import { SearchParam } from "../types";
import { ServiceErrorCode } from "../utils/httpUtils";
import type { UserID } from "./useAccount";
import type { User } from "./useProject";
import type {
  InvitationID,
  ProjectViewLinkGetInvitationResponseData,
  ProjectViewLinkSpaceTypes,
  SpaceID,
} from "./useProjectViewLink";

export type ProjectSharingBaseRole = "viewer" | "commenter";
export const ProjectSharingBaseRoles = {
  Viewer: "viewer",
  Commenter: "commenter",
} as const satisfies { [key in string]: ProjectSharingBaseRole };
export const ProjectSharingBaseRolesDisplayNames: { [key in ProjectSharingBaseRole]: string } = {
  viewer: "Viewer",
  commenter: "Commenter",
} as const;

export type ProjectMember = {
  email: string;
  baseRole: ProjectSharingBaseRole;
  userId: UserID;
  name?: string;
};

export type ProjectInvitation = {
  email: string;
  baseRole: ProjectSharingBaseRole;
  invitationId: InvitationID;
};

export type ProjectSharingData = {
  users: {
    email: string;
    baseRole: ProjectSharingBaseRole;
    userId?: UserID;
    invitationId?: InvitationID;
    name?: string;
  }[];
  space: {
    spaceId: SpaceID;
    spaceName?: string;
    spaceType: (typeof ProjectViewLinkSpaceTypes)[keyof typeof ProjectViewLinkSpaceTypes];
    spaceUsers?: User[];
  };
};

export type ProjectInvitationRequestPayload = {
  emails: string[];
  baseRole: ProjectSharingBaseRole;
};

export type ProjectInvitationResponse = {
  invitedUsers: {
    email: string;
    baseRole: string;
    userId?: string;
    invitationId?: string;
    name?: string;
  }[];
  errors: {
    email: string;
    code: string;
    message: string;
  }[];
};

export const projectSharingQueryKey = ["projectSharingQueryKey"];
export const projectInvitationQueryKey = ["projectInvitationQueryKey"];

export const useProjectInvitation = () => {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const invitationHash = searchParams.get(SearchParam.InvitationHash) ?? "";

  const enabled = Boolean(invitationHash);

  const query = useQuery({
    queryKey: [...projectInvitationQueryKey],
    queryFn: async () => {
      const request = isLoggedIn()
        ? SyncRequestClient.authenticatedRequest
        : SyncRequestClient.publicRequest;

      const response = await request({
        method: "GET",
        url: `/web/projects/${projectId}/invitations-by-hash/${invitationHash}`,
      });

      return response.json<ProjectViewLinkGetInvitationResponseData>();
    },
    enabled,
  });

  return { ...query, enabled };
};

export const useMutateAcceptProjectInvitation = () => {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const invitationHash = searchParams.get(SearchParam.InvitationHash) ?? "";
  return useMutation({
    mutationFn: async ({ emailToken }: { emailToken?: string | null } = {}) => {
      try {
        const response = await SyncRequestClient.authenticatedRequest({
          method: "POST",
          url: `/web/projects/${projectId}/invitations-by-hash/${invitationHash}/accept`,
          body: { ...(emailToken && { emailToken }) },
        });
        return response;
      } catch (error) {
        // Accept invitation is best effort.
        // Not found could mean you can view the project or could mean you can't.
        // Up to the project loading to decide and show the proper error screen.
        if (
          error instanceof ServiceError &&
          (error.hasCode(ServiceErrorCode.NotFound) ||
            error.hasCode(ServiceErrorCode.InvitationNotFound))
        ) {
          return;
        }
        throw error;
      }
    },
  });
};

export const useProjectSharing = () => {
  const { projectId } = useParams();
  return useQuery({
    queryKey: [...projectSharingQueryKey, projectId],
    queryFn: async () => {
      const response = await SyncRequestClient.authenticatedRequest({
        method: "GET",
        url: `/web/projects/${projectId}/sharing`,
      });
      return response.json<ProjectSharingData>();
    },
    enabled: isLoggedIn() && Boolean(projectId),
  });
};

export const useUpdateProjectSharingUser = ({
  onSuccess,
  onError,
}: {
  onSuccess: (data: ProjectInvitationResponse) => void;
  onError?: (error: Error) => void;
}) => {
  const { projectId } = useParams();
  return useMutation({
    mutationFn: async ({ userId, baseRole }: { userId: string; baseRole: string }) => {
      const response = await SyncRequestClient.authenticatedRequest({
        method: "POST",
        url: `/web/projects/${projectId}/users/${userId}`,
        body: {
          baseRole,
        },
      });
      return response.json<ProjectInvitationResponse>();
    },
    onSuccess,
    onError,
  });
};
export const useDeleteProjectSharingUser = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError?: (error: Error) => void;
}) => {
  const { projectId } = useParams();
  return useMutation({
    mutationKey: [...projectSharingQueryKey, projectId],
    mutationFn: async ({ userId }: { userId: UserID }) => {
      await SyncRequestClient.authenticatedRequest({
        method: "DELETE",
        url: `/web/projects/${projectId}/users/${userId}`,
      });
    },
    onSuccess,
    onError,
  });
};

export const useCreateProjectInvitation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: ProjectInvitationResponse) => void;
  onError?: (error: Error) => void;
}) => {
  const { projectId } = useParams();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [...projectSharingQueryKey, projectId],
    mutationFn: async (invitations: ProjectInvitationRequestPayload) => {
      const response = await SyncRequestClient.authenticatedRequest({
        method: "POST",
        url: `/web/projects/${projectId}/invitations`,
        body: invitations,
      });
      return response.json<ProjectInvitationResponse>();
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        [...projectSharingQueryKey, projectId],
        (oldData: ProjectSharingData | undefined) => {
          if (!oldData) return oldData;
          const newUsers = data.invitedUsers.map((user) => ({
            email: user.email,
            baseRole: user.baseRole,
            userId: user.userId,
            invitationId: user.invitationId,
            name: user.name,
          }));
          return {
            ...oldData,
            users: [...newUsers, ...oldData.users],
          };
        }
      );
      onSuccess?.(data);
    },
    onError,
  });
};

export const useUpdateProjectInvitation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) => {
  const { projectId } = useParams();
  return useMutation({
    mutationFn: async (data: { projectInvitationId: string; baseRole: ProjectSharingBaseRole }) => {
      await SyncRequestClient.authenticatedRequest({
        method: "PUT",
        url: `/web/projects/${projectId}/invitations/${data.projectInvitationId}`,
        body: {
          baseRole: data.baseRole,
        },
      });
    },
    onSuccess,
    onError,
  });
};

export const useDeleteProjectInvitation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (invitationId: InvitationID) => void;
  onError?: (error: Error) => void;
}) => {
  const { projectId } = useParams();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (projectInvitationId: InvitationID) => {
      await SyncRequestClient.authenticatedRequest({
        method: "DELETE",
        url: `/web/projects/${projectId}/invitations/${projectInvitationId}`,
      });
      return projectInvitationId;
    },
    onSuccess: (invitationId: InvitationID) => {
      onSuccess?.(invitationId);
    },
    onError,
  });

  const removeInvitationFromCache = (invitationId: InvitationID) => {
    queryClient.setQueryData(
      [...projectSharingQueryKey, projectId],
      (oldData: ProjectSharingData | undefined) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          users: oldData.users.filter((user) => user.invitationId !== invitationId),
        };
      }
    );
  };

  return {
    ...mutation,
    removeInvitationFromCache,
  };
};

export const useUpdateProjectUser = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) => {
  const { projectId } = useParams();
  return useMutation({
    mutationFn: async ({
      userId,
      baseRole,
    }: {
      userId: UserID;
      baseRole: ProjectSharingBaseRole;
    }) => {
      await SyncRequestClient.authenticatedRequest({
        method: "PUT",
        url: `/web/projects/${projectId}/users/${userId}`,
        body: {
          baseRole: baseRole,
        },
      });
    },
    onSuccess,
    onError,
  });
};

export const useDeleteProjectUser = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) => {
  const { projectId } = useParams();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (userId: UserID) => {
      await SyncRequestClient.authenticatedRequest({
        method: "DELETE",
        url: `/web/projects/${projectId}/users/${userId}`,
      });
      return userId;
    },
    onSuccess: () => {
      onSuccess?.();
    },
    onError,
  });

  const removeUserFromCache = (userId: UserID) => {
    queryClient.setQueryData(
      [...projectSharingQueryKey, projectId],
      (oldData: ProjectSharingData | undefined) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          users: oldData.users.filter((user) => user.userId !== userId),
        };
      }
    );
  };

  return {
    ...mutation,
    removeUserFromCache,
  };
};
